/**
 * Baglanti durumu - React'in DISINDA, `streamStore` ile ayni gerekceyle.
 *
 * ## Neden gerekli
 *
 * `runStream` zaten jitter'li backoff ile yeniden baglaniyordu ama bunu
 * ARAYUZE SOYLEMIYORDU. Kullanicinin gordugu sey: yanit bir yerde duruyor ve
 * hicbir sey olmuyor. Uygulama calisiyor, kullanici bozuk sandigi icin sayfayi
 * yeniliyor - ve yenileme dogru olan seyi (sessizce yeniden baglanmayi)
 * gereksiz kiliyor.
 *
 * Uc durum ayirt ediliyor ve ucu de farkli bir sey soylemek zorunda:
 *
 *   * `offline`   -> tarayici agda degil. "Yeniden dene" gostermek anlamsiz.
 *   * `retrying`  -> ag var, sunucuya ulasilamiyor. Kacinci deneme onemli:
 *                    kullanici bir seyin OLDUGUNU gormeli.
 *   * `lost`      -> denemeler tukendi. Artik ACIK bir "Tekrar dene" gerekiyor;
 *                    sessizce sonsuza kadar donmek en kotu secenek.
 */

export type ConnectionState = 'ok' | 'offline' | 'retrying' | 'lost'

export interface ConnectionStatus {
  state: ConnectionState
  /** `retrying` iken kacinci deneme. */
  attempt: number
}

type Listener = () => void

const listeners = new Set<Listener>()
let status: ConnectionStatus = { state: 'ok', attempt: 0 }
// Her degisimde artan sayac: `useSyncExternalStore` anlik goruntuyu
// `Object.is` ile karsilastiriyor ve mutable nesne dondurmek re-render'i
// ATLAMASINA yol aciyor (bkz. `streamStore.getSnapshot`).
let version = 0

export function getStatus(): ConnectionStatus {
  return status
}

export function getVersion(): number {
  return version
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function set(next: ConnectionStatus): void {
  // Ayni durumda bildirim YAPMIYORUZ: her backoff turunda re-render etmek
  // bosa is (ve `retrying` sirasinda saniyede birden fazla olabiliyor).
  if (next.state === status.state && next.attempt === status.attempt) return
  status = next
  version += 1
  for (const listener of listeners) listener()
}

/** Akis basarili: her sey yolunda. */
export function markConnected(): void {
  set({ state: 'ok', attempt: 0 })
}

/** Yeniden baglanma denemesi. */
export function markRetrying(attempt: number): void {
  // `navigator.onLine` GUVENILIR DEGIL (kablosuz agda "bagli" ama internet
  // yok olabilir) - o yuzden yalnizca KESIN offline durumunu ondan
  // ogreniyoruz; false negatifi yok, false pozitifi var.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    set({ state: 'offline', attempt })
    return
  }
  set({ state: 'retrying', attempt })
}

/** Denemeler tukendi. */
export function markLost(): void {
  set({ state: 'lost', attempt: 0 })
}

/** Testler icin. */
export function reset(): void {
  status = { state: 'ok', attempt: 0 }
  version = 0
  listeners.clear()
}

/**
 * Tarayici cevrimdisi olaylarini dinler. Uygulama acilisinda BIR KEZ.
 *
 * `offline` -> hemen bildiriyoruz: kullanici neden yanit gelmedigini
 * ANINDA bilmeli. `online` -> `ok`a DONMUYORUZ; gercekten baglandigimizi
 * yalnizca basarili bir istek kanitlar. Aksi halde "bagli" gosterip yine
 * hicbir sey akmayan bir arayuz uretirdik.
 */
export function watchBrowserConnectivity(): () => void {
  if (typeof window === 'undefined') return () => {}

  const onOffline = () => set({ state: 'offline', attempt: 0 })
  const onOnline = () => {
    if (status.state === 'offline') set({ state: 'retrying', attempt: status.attempt })
  }

  window.addEventListener('offline', onOffline)
  window.addEventListener('online', onOnline)
  if (navigator.onLine === false) onOffline()

  return () => {
    window.removeEventListener('offline', onOffline)
    window.removeEventListener('online', onOnline)
  }
}
